import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Send, MessageSquare, ArrowLeft, Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClientMessagesPage() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null); // client_id
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fetchConvs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/client-messages`);
      setConvs(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchThread = useCallback(async (clientId) => {
    const res = await axios.get(`${API}/admin/client-messages/${clientId}`);
    setMessages(res.data || []);
  }, []);

  useEffect(() => {
    fetchConvs();
    const t = setInterval(() => {
      fetchConvs();
      if (selected) fetchThread(selected).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [fetchConvs, fetchThread, selected]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const openThread = async (clientId) => {
    setSelected(clientId);
    setMessages([]);
    await fetchThread(clientId);
    await fetchConvs();
  };

  const send = async () => {
    const body = text.trim();
    if (!body || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/client-messages/${selected}`, { text: body });
      setText('');
      await fetchThread(selected);
      await fetchConvs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const current = convs.find((c) => c.client_id === selected);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-5xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">CLIENT MESSAGES</h1>
            <p className="text-xs text-muted-foreground mt-1">Direct conversations with your clients.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Conversation list */}
          <Card className={cn('bg-card border-border rounded-sm md:col-span-1', selected && 'hidden md:block')}>
            <CardContent className="p-0 divide-y divide-border max-h-[65vh] overflow-y-auto" data-testid="conversations-list">
              {loading ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
              ) : convs.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No clients yet.</p>
              ) : (
                convs.map((c) => (
                  <button
                    key={c.client_id}
                    type="button"
                    onClick={() => openThread(c.client_id)}
                    className={cn(
                      'w-full text-left p-3.5 hover:bg-accent/30 transition-colors',
                      selected === c.client_id && 'bg-accent/40'
                    )}
                    data-testid={`conv-${c.client_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-primary shrink-0" />
                        {c.company_name}
                      </p>
                      {c.unread > 0 && (
                        <Badge className="bg-primary text-primary-foreground text-[10px] shrink-0" data-testid={`unread-${c.client_id}`}>
                          {c.unread}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {c.last_text ? `${c.last_sender === 'admin' ? 'You: ' : ''}${c.last_text}` : 'No messages yet'}
                    </p>
                    {c.last_at && (
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                        {(() => { try { return format(parseISO(c.last_at), 'dd MMM HH:mm'); } catch { return ''; } })()}
                      </p>
                    )}
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Thread */}
          <Card className={cn('bg-card border-border rounded-sm md:col-span-2', !selected && 'hidden md:block')}>
            <CardContent className="p-0 flex flex-col" style={{ height: '65vh' }}>
              {!selected ? (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                  Select a client to open the conversation
                </div>
              ) : (
                <>
                  <div className="border-b border-border px-4 py-2.5 flex items-center gap-2">
                    <Button size="icon" variant="ghost" onClick={() => setSelected(null)} className="h-7 w-7 md:hidden" data-testid="thread-back-btn">
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <p className="text-sm font-semibold text-foreground">{current?.company_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{current?.email}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-messages-list">
                    {messages.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-8">No messages yet — start the conversation.</p>
                    ) : (
                      messages.map((m) => (
                        <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`} data-testid={`admin-msg-${m.id}`}>
                          <div className={cn(
                            'max-w-[80%] rounded-sm px-3 py-2',
                            m.sender_role === 'admin' ? 'bg-primary/15 border border-primary/30 text-foreground' : 'bg-muted/40 border border-border text-foreground'
                          )}>
                            <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {m.sender_name} · {(() => { try { return format(parseISO(m.created_at), 'dd MMM HH:mm'); } catch { return ''; } })()}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={bottomRef} />
                  </div>
                  <div className="border-t border-border p-3 flex gap-2 items-end">
                    <Textarea
                      rows={1}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Type a reply…"
                      className="rounded-sm resize-none min-h-[42px] bg-background border-border"
                      data-testid="admin-msg-input"
                    />
                    <Button
                      onClick={send}
                      disabled={sending || !text.trim()}
                      className="h-10 bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm shrink-0"
                      data-testid="admin-msg-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
